import { Breadcrumb } from "@/components/Common/Breadcrumb";
import Loading from "@/components/Common/Loading";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useGetDeploy } from "@/hooks/useDeploys";
import { getDeployStatusColor, getDeployStatusIcon } from "@/lib/deploy/utils";
import { APPLICATION_CATEGORY, PATH } from "@/lib/enums";
import { humanizeString } from "@/lib/utils";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { CustomDomainsDialog } from "../Deploy/CustomDomainsDialog";
import { DeleteConfirmDialog } from "../Deploy/DeleteDialog";
import { RedeployConfirmDialog } from "../Deploy/RedeployConfirmDialog";
import { BuildLogsViewer } from "../Deploy/BuildLogsViewer";
import { RuntimeLogsViewer } from "../Deploy/RuntimeLogsViewer";
import { SettingsTab } from "../Deploy/SettingsTab";
import { OverviewTab } from "../Deploy/OverviewTab";

export function ApplicationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const deployQuery = useGetDeploy(id!);
  const { toast } = useToast();
  const deploy = deployQuery.data;

  // State management
  const [activeTab, setActiveTab] = useState("overview");
  const [showRedeployDialog, setShowRedeployDialog] = useState(false);
  const [showDomainsDialog, setShowDomainsDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Breadcrumb configuration
  const breadcrumbs = useMemo(() => {
    if (!deploy) return [];

    const category =
      deploy.deployType === "cdn-site"
        ? APPLICATION_CATEGORY.STATIC_SITES
        : deploy.deployType === "managed-database"
          ? APPLICATION_CATEGORY.DATABASES
          : APPLICATION_CATEGORY.APPLICATIONS;

    const categoryPath =
      category === APPLICATION_CATEGORY.STATIC_SITES
        ? `${PATH.APPLICATIONS}?category=${APPLICATION_CATEGORY.STATIC_SITES}`
        : category === APPLICATION_CATEGORY.DATABASES
          ? `${PATH.APPLICATIONS}?category=${APPLICATION_CATEGORY.DATABASES}`
          : PATH.APPLICATIONS;

    const categoryLabel =
      category === APPLICATION_CATEGORY.STATIC_SITES
        ? "Static Sites"
        : category === APPLICATION_CATEGORY.DATABASES
          ? "Databases"
          : "Applications";

    return [
      { label: "home", href: PATH.HOME },
      { label: categoryLabel, href: categoryPath },
      { label: deploy.repoName },
    ];
  }, [deploy]);

  // Tab configuration
  const tabs = [
    { value: "overview", label: "Overview" },
    { value: "build-logs", label: "Build Logs" },
    { value: "runtime-logs", label: "Runtime Logs" },
    { value: "environment-variables", label: "Environment Variables" },
    { value: "domains", label: "Domains" },
  ];

  // Utility functions
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied!",
      description: "Value copied to clipboard.",
    });
  };

  const openInNewPage = (url: string) => {
    const fullUrl = url.startsWith("http") ? url : `https://${url}`;
    window.open(fullUrl, "_blank", "noopener,noreferrer");
  };

  // Loading and error states
  if (deployQuery.error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-2">
          <h3 className="text-lg font-medium text-[var(--text-main)]">
            Application not found
          </h3>
          <p className="text-[var(--text-subtle)]">
            The application you're looking for doesn't exist or you don't have
            permission to view it.
          </p>
        </div>
      </div>
    );
  }

  if (deployQuery.isLoading || !deploy) {
    return <Loading />;
  }

  return (
    <>
      <div className="flex min-h-screen flex-col bg-[var(--bg)] text-[var(--text-main)] overflow-hidden">
        {/* Header Section */}
        <div className="border-b border-[var(--border-color)] bg-[var(--bg)]">
          <div className="w-[80%] mx-auto box-border px-4 sm:px-6 lg:px-10 py-8">
            <div className="flex flex-col gap-6">
              {/* Breadcrumbs */}
              <div className="hidden lg:block font-mono text-xs uppercase tracking-wide opacity-70">
                <Breadcrumb items={breadcrumbs} />
              </div>

              {/* Title & Meta Area - Split Layout */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="space-y-1.5">
                  <h1 className="text-3xl font-bold tracking-tight text-[var(--text-main)] font-mono">
                    {deploy.repoName}
                  </h1>
                  <p className="text-xs text-[var(--text-subtle)] font-mono tracking-wide flex items-center gap-2">
                    ID: {id}
                    <span className="w-2 h-2 rounded-full bg-[var(--primary)]/50 animate-pulse"></span>
                  </p>
                </div>

                {/* Status Badge - Right aligned on desktop */}
                <div className="flex items-center">
                  <Badge
                    variant="outline"
                    className={`${getDeployStatusColor(deploy.status)} 
                      bg-opacity-10 border-opacity-20 px-3 py-1 text-xs font-mono font-medium uppercase tracking-wider transition-all`}
                  >
                    <span className="flex items-center gap-2">
                      {getDeployStatusIcon(deploy.status)}
                      {humanizeString(deploy.status)}
                    </span>
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main content area */}
        <div
          className="flex-1 overflow-y-auto bg-[var(--bg)]"
          style={{ scrollbarGutter: "stable" }}
        >
          <div className="w-[80%] mx-auto box-border px-4 sm:px-6 lg:px-10 py-8">
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="space-y-6 w-full"
            >
              {/* Tabs List - Centered */}
              <div className="border-b border-[var(--border-color)] mb-8">
                <TabsList className="bg-transparent p-0 w-full flex justify-center gap-8 h-auto rounded-none">
                  {tabs.map((tab) => (
                    <TabsTrigger
                      key={tab.value}
                      value={tab.value}
                      className="
                        relative h-10 rounded-none border-b-2 border-transparent bg-transparent px-2 pb-3 pt-2 
                        text-xs font-mono uppercase tracking-widest font-medium text-[var(--text-subtle)] shadow-none transition-all
                        data-[state=active]:border-[var(--primary)] data-[state=active]:text-[var(--primary)]
                        data-[state=active]:shadow-none hover:text-[var(--text-main)]
                        focus-visible:ring-0 focus-visible:ring-offset-0
                      "
                    >
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>

              {/* Tab Contents - Content widths preserved */}
              <div className="pt-2">
                <TabsContent
                  value="overview"
                  className="w-full max-w-5xl mx-auto focus-visible:outline-none focus-visible:ring-0"
                >
                  <OverviewTab
                    deploy={deploy}
                    onRedeploy={() => setShowRedeployDialog(true)}
                    onViewLogs={() => setActiveTab("build-logs")}
                    onDelete={() => setShowDeleteDialog(true)}
                    onManageDomains={() => setShowDomainsDialog(true)}
                    onCopyToClipboard={copyToClipboard}
                    onOpenInNewPage={openInNewPage}
                  />
                </TabsContent>

                <TabsContent
                  value="build-logs"
                  className="w-full max-w-5xl mx-auto focus-visible:outline-none focus-visible:ring-0"
                >
                  <BuildLogsViewer deploy={deploy} />
                </TabsContent>

                <TabsContent
                  value="runtime-logs"
                  className="w-full max-w-5xl mx-auto focus-visible:outline-none focus-visible:ring-0"
                >
                  <RuntimeLogsViewer deploy={deploy} />
                </TabsContent>

                <TabsContent
                  value="environment-variables"
                  className="w-full max-w-5xl mx-auto focus-visible:outline-none focus-visible:ring-0"
                >
                  <SettingsTab
                    deploy={deploy}
                    onManageDomains={() => setShowDomainsDialog(true)}
                    section="environment-variables"
                  />
                </TabsContent>

                <TabsContent
                  value="domains"
                  className="w-full max-w-5xl mx-auto focus-visible:outline-none focus-visible:ring-0"
                >
                  <SettingsTab
                    deploy={deploy}
                    onManageDomains={() => setShowDomainsDialog(true)}
                    section="domains"
                  />
                </TabsContent>
              </div>
            </Tabs>
          </div>
        </div>

        {/* Dialogs */}
        {showRedeployDialog && (
          <RedeployConfirmDialog
            deploy={deploy}
            open={showRedeployDialog}
            onOpenChange={setShowRedeployDialog}
          />
        )}

        {showDomainsDialog && (
          <CustomDomainsDialog
            deploy={deploy}
            open={showDomainsDialog}
            onOpenChange={setShowDomainsDialog}
          />
        )}

        {showDeleteDialog && (
          <DeleteConfirmDialog
            deploy={deploy}
            open={showDeleteDialog}
            onOpenChange={setShowDeleteDialog}
          />
        )}
      </div>
    </>
  );
}
